import React, { memo } from 'react';

import { I18nText } from '../../../../../types';
import {
  FetchOperationsFilterOperations,
  FetchOperationsFilters,
  FetchOperationsSort,
  FetchOperationsSortOperations,
  FetchOperationsSortableFields
} from '../../../operationsTypes';
import { UserID } from '../../../../users/usersTypes';

import { useCompanyOperationsCompactTableHeaderFilters } from './hooks/useCompanyOperationsCompactTableHeaderFilters';

import { IndexTableHeader } from '../../../../common/components/tables/IndexTableHeader';
import { IndexTableHeaderEmptyCell } from '../../../../common/components/tables/IndexTableHeaderEmptyCell';

import { CompactIndexTableHeaderSortFilterCell } from '../../../../common/components/tables/CompactIndexTableHeaderSortFilterCell';
import { CompactIndexTableHeaderSortFilterSearchCell } from '../../../../common/components/tables/CompactIndexTableHeaderSortFilterSearchCell';

import { generateNanoId } from '../../../../../utils/generateNanoId';

import { operationsKeys, words } from '../../../../../locales/keys';

interface CompanyOperationsCompactTableHeaderProps {
  changeOperationsFilters: FetchOperationsFilterOperations;
  sortOperations: FetchOperationsSortOperations;
  operationsFilters: FetchOperationsFilters;
  operationsSort: FetchOperationsSort;
  clientsIds?: UserID[];
  withUpdateButton?: boolean;
  customI18nCompleteDatetimeTitle?: I18nText;
  customI18nDateTitle?: I18nText;
}

function CompanyOperationsCompactTableHeader({
  changeOperationsFilters,
  sortOperations,
  operationsFilters,
  operationsSort,
  clientsIds,
  withUpdateButton,
  customI18nCompleteDatetimeTitle,
  customI18nDateTitle
}: CompanyOperationsCompactTableHeaderProps) {
  const viewId = generateNanoId();

  const {
    clientFilterFields,
    createdFilterFields,
    dateFilterFields,
    completedFilterFields,
    subjectFilterFields,
    stateFilterFields,
    typeFilterFields,
    userFilterFields
  } = useCompanyOperationsCompactTableHeaderFilters({
    clientsIds,
    customI18nCompleteDatetimeTitle,
    customI18nDateTitle
  });

  const withDropdown = withUpdateButton;

  return (
    <IndexTableHeader withoutCheckboxes>
      {clientFilterFields ? (
        <CompactIndexTableHeaderSortFilterCell
          currentFilters={operationsFilters}
          filterFields={clientFilterFields}
          onFilter={changeOperationsFilters}
          viewId={viewId}
          className="w-0 relative hover:bg-gray-100 dark:hover:bg-gray-800 pl-3 pr-1 py-2 dark:border-gray-700 border-gray-200 border-b"
        />
      ) : (
        <IndexTableHeaderEmptyCell />
      )}

      <CompactIndexTableHeaderSortFilterCell
        currentFilters={operationsFilters}
        currentSort={operationsSort}
        filterFields={typeFilterFields}
        onFilter={changeOperationsFilters}
        onSort={sortOperations}
        sortableField={FetchOperationsSortableFields.OPERATION_TYPE}
        viewId={viewId}
        className="relative hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-2 w-px border-b"
      />

      <CompactIndexTableHeaderSortFilterSearchCell
        currentFilters={operationsFilters}
        currentSort={operationsSort}
        filterFields={subjectFilterFields}
        i18nText={operationsKeys.record}
        onFilter={changeOperationsFilters}
        onSort={sortOperations}
        sortableField={FetchOperationsSortableFields.SUBJECT}
        searchField={FetchOperationsSortableFields.SUBJECT}
        viewId={viewId}
      />

      <CompactIndexTableHeaderSortFilterCell
        currentFilters={operationsFilters}
        currentSort={operationsSort}
        filterFields={stateFilterFields}
        i18nText={operationsKeys.status}
        onFilter={changeOperationsFilters}
        onSort={sortOperations}
        sortableField={FetchOperationsSortableFields.STATE}
        viewId={viewId}
      />

      <CompactIndexTableHeaderSortFilterCell
        currentFilters={operationsFilters}
        filterFields={userFilterFields}
        i18nText={words.author}
        onFilter={changeOperationsFilters}
        viewId={viewId}
      />

      <CompactIndexTableHeaderSortFilterCell
        currentFilters={operationsFilters}
        currentSort={operationsSort}
        filterFields={createdFilterFields}
        i18nText={operationsKeys.recorded}
        onFilter={changeOperationsFilters}
        onSort={sortOperations}
        sortableField={FetchOperationsSortableFields.CREATED_AT}
        viewId={viewId}
      />

      <CompactIndexTableHeaderSortFilterCell
        currentFilters={operationsFilters}
        currentSort={operationsSort}
        filterFields={completedFilterFields}
        i18nText={customI18nCompleteDatetimeTitle || operationsKeys.completed}
        onFilter={changeOperationsFilters}
        onSort={sortOperations}
        sortableField={FetchOperationsSortableFields.COMPLETE_DATETIME}
        viewId={viewId}
      />

      <CompactIndexTableHeaderSortFilterCell
        currentFilters={operationsFilters}
        currentSort={operationsSort}
        filterFields={dateFilterFields}
        i18nText={customI18nDateTitle || operationsKeys.dueDate}
        onFilter={changeOperationsFilters}
        onSort={sortOperations}
        sortableField={FetchOperationsSortableFields.DATE}
        viewId={viewId}
      />

      {withDropdown ? <IndexTableHeaderEmptyCell /> : null}
    </IndexTableHeader>
  );
}

export default memo(CompanyOperationsCompactTableHeader);
